import { useState, createContext, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../server/api';
import Logger from '../utils/logger';
import { STORAGE } from '../defs/storage';
import { getStorageValue, setStorageValue } from '../hooks/useLocalStorage';
import Point from '../models/point/Point';
import Schedule from '../models/schedule/Schedule';
import User from '../models/user/User';
import UserSession from '../models/session/UserSession';
import Place from '../models/place/Place';
import { RepositoryContext } from './RepositoryProvider';
import ClientDetails from '../models/user/ClientDetails';
import Order from '../models/order/Order';
import Account from '../models/account/Account';
import { AutocompleteItemTemplate } from '../models/AutocompleteItem';
import DateTZ, { TIMEZONE } from '../models/date/DateTZ';
import log from '../utils/logger';
import { useCookies } from 'react-cookie';
import Price from '../models/price/Price';
import ReportParams from "../models/report/ReportParams";
import Collection from "../models/collection/Collection";

export const UserContext = createContext();

const initialState = {
  user: null,
  account: null,
  point: null,
  session: null,
  scheduleDates: [],
  scheduleTimes: [],
  schedules: [],
  points: [],
  places: [],
  prices: [],
};

export const UserProvider = (props) => {
  const logger = new Logger({ name: 'UserProvider', showDate: false, showFile: false });
  const {t} = useTranslation(['common', 'account']);

  const [userSession, setUserSession] = useState(getStorageValue(STORAGE.SESSION), new UserSession({}));
  const [userInfo, setUserInfo] = useState(initialState);
  const [login, setLogin] = useState(false);

  const [, addCookie, removeCookie] = useCookies(['session_id', 'x-service']);

  const repository = useContext(RepositoryContext);

  const _setSession = (field, value) => {
    setUserSession((prevState) => ({ ...prevState, [field]: value }));

    if (userInfo.session === null || userInfo.session[field] !== value) {
      let session = new UserSession({ ...getStorageValue(STORAGE.SESSION), [field]: value });
      setStorageValue(STORAGE.SESSION, session);
      _setState('session', session);
    }
  };

  const _useSession = (...args) => {
    const [field] = args;

    useEffect(() => {
      if (args.length > 1) {
        _setSession(field, args[1]);
      } else {
        _setSession(field, (userSession && userSession[field]) ?? null);
      }
    }, []);

    return [(userSession && userSession[field]) ?? null, (value) => _setSession(field, value)];
  };


  const _setState = (field, value) => {
    setUserInfo((prevState) => ({ ...prevState, [field]: value }));
  };

  const _useState = (...args) => {
    const [field] = args;

    useEffect(() => {
      if (args.length > 1) {
        _setState(field, args[1]);
      } else {
        _setState(field, userInfo[field]);
      }
    }, []);

    return [userInfo[field], (value) => _setState(field, value)];
  };


  const fetch = {
    user: (forward = false) => {
      if (!forward && userInfo.user) {
        return Promise.resolve(userInfo.user);
      } else {
        return repository.action(API.user.whoami).then((response) => {
          let user = null;
          let accountId = '';
          if (response.error) {
            //TODO:: handle error
            setUser(null);
          } else {
            user = setUser(response);
            let newUserSession = new UserSession();

            if (user) {
              _setSession('logged_in', true);

              let account = null;

              if (userSession.account) {
                account = user.accounts.find((account) => account.account.id === userSession.account);
              }

              if (!account && user.accounts.length > 0) {
                account = user.accounts[0];
              }

              accountId = account ? account.account.id : '';

              let role = userSession.role;
              if (account && (!role || account.roles.indexOf(role) === -1)) {
                role = account.roles[0];
              } else if (!account) {
                role = '';
              }


              if (!account) {
                newUserSession.point = '';
                newUserSession.subdivision = '';
              }

              newUserSession.account = accountId;
              newUserSession.user = user.id;
              _setState('account', account);
              addCookie('x-service', account?.accountType);

              _setState('user', user);

              newUserSession.role = role;

              if (accountId && userSession.account !== accountId && newUserSession.isCashier()) {
                loadPoints(newUserSession, user).catch(() => {});
              }

              if (accountId && (newUserSession.isCashier() || newUserSession.isGuide())) {
                fetch.prices(accountId);
              }

              let point = userSession.point;
              if (account && (point || account.roles.indexOf(role) === -1)) {
                role = account.roles[0];
              }
            } else {
              _setState('account', null);
              removeCookie('x-service');
              _setState('point', null);
              _setSession('point', '');
              _setSession('subdivision', '');
            }

            _setSession('role', newUserSession.role);
            _setSession('user', newUserSession.user);
            _setSession('account', newUserSession.account);
        }
          return { user, accountId };
        }).catch((e) => {
          logger.error(e);
          return Promise.reject(e);
        });
      }
    },

    points: ({ accountId }) => {
      return repository.list(API.point.autocompleteList, { accountId: accountId || userSession.account }, AutocompleteItemTemplate(Point))
        .then((rows) => rows)
        .catch((e) => []);
    },

    schedules: (day) => {
      return new Promise((resolve, reject) => {
        let date = new DateTZ().format('YYYY-MM-DD');
        if (day) {
          date = new DateTZ(day).format('YYYY-MM-DD');
        }

        let subdivision = userSession.subdivision;

        if (subdivision) {
          repository.action(API.excursion.excursions, {date: date, timezone: TIMEZONE, subdivision}).then((response) => {
            resolve(setSchedules(response));
          }).catch(reject);
        } else {
          resolve([]);
        }
      });
    },

    scheduleDates: (excursionId, subdivisionId) => {
      return repository.action(API.schedule.datesSeats, { accountId: userSession.account, id: excursionId, subdivision: subdivisionId }, String).then((dates) => {
        setScheduleDates(dates);
        return dates;
      });
    },

    scheduleTimes: (excursionId, day) => {
      return repository.list(API.schedule.times, { id: excursionId, date: new DateTZ(day || undefined).format('YYYY-MM-DD'), accountId: userSession.account }, Object).then((times) => {
        setScheduleTimes(times);
        return times;
      });
    },

    place: (id) => {
      return repository.action(API.place.place, {id}).then((response) => {
        let place = new Place(response);
        setPlaces([ ...userInfo.places, place ]);
        return place;
      });
    },

    prices: (accountId) => {
      return repository.list(API.price.prices, { accountId: accountId || userSession.account, tab: 'excursion' }, Price)
        .then((rows) => rows)
        .catch((e) => {
          log.error(e);
          return [];
        });
    },
  };

  const reset = () => {
    setUserSession(initialState);
  };

  const setLoggedIn = (logged_in) => {
    userSession.logged_in = logged_in;
    setLogin(logged_in);
    _setSession('logged_in', logged_in);
  };

  const getSession = () => {
    return userInfo.session || getStorageValue(STORAGE.SESSION, userSession, UserSession);
  };

  const isLoggedIn = () => {
    return getSession().logged_in && !!getSession().user;
  };


  const setUser = (prop = null) => {
    let user = prop === null ? null : prop instanceof User ? prop : new User({ ...userInfo.user, ...prop });
    _setState('user', user);
    _setSession('user', user ? user.id : null);
    return user;
  };

  const getUser = () => {
    return new Promise((resolve, reject) => {
      let user = userInfo.user;

      let logged_in = userSession ? userSession.logged_in : (userInfo.session ? userInfo.session.logged_in : getSession().logged_in);
      if (!logged_in || ((user === null || !user.isValid()) && logged_in)) {
        loadUser().then((user) => {
          resolve(user);
          return user;
        }).catch((e) => {
          log.error(e);
          reject(e);
          return e;
        });
      } else if (logged_in) {
        resolve(user);
      } else {
        reject('FAIL');
      }
    });
  };

  const loadUser = () => {
    return fetch.user().then(({user, accountId}) => {
      return getSession().isCashier() ? loadPoints(getSession(), user).then(() => user).catch((e) => user) : user;
    }).catch((e) => null);
  };

  const setAccount = (prop = null) => {
    let account = prop === null ? null : prop instanceof Account ? prop : new Account({ ...userSession.account, ...prop });
    setUserSession((prevState) => { prevState.account = account; return prevState; });
    return account;
  };

  const loadPoints = ({account, point}, user) => {
    return new Promise((resolve, reject) => {
      if (account) {
        repository.list(API.cashier.schedules, { accountId: account || userSession.account }).then((schedules) => {
          let currentPoint = null;
          schedules.forEach((s) => {
            if (s.isNow() && !currentPoint) {
              currentPoint = s.point.id;
            }
          });

          fetch.points({ accountId: account }).then((points) => {
            if (points) {
              const userAccount = (user || userInfo.user)?.accounts?.filter((a) => a.account.id === account);
              const userSubdivisions = userAccount.length > 0 ? userAccount[0].subdivisions : [];
              if (userSubdivisions && userSubdivisions.length > 0) {
                points = points.filter((p) => userSubdivisions.indexOf(p.meta.subdivision.id) >= 0);
              }

              setPoints(points);
              let _point = points.find((p) => (currentPoint !== null) ? ( p.meta.id === currentPoint) : (p.meta.id === point));
              if (_point) {
                _setState('point', _point.meta);
                _setSession('point', _point.meta.id);
                _setSession('subdivision', _point.meta.subdivision.id);
              } else if (!point || !_point) {
                _setSession('point', points.length ? points[0].meta.id : '');
                _setState('point', points.length ? points[0].meta : null);
                _setSession('subdivision', points.length ? points[0].meta.subdivision.id : '');
              }
              resolve(points);
            } else {
              resolve([]);
            }

            return points || [];
          }).catch((e) => {
            logger.error('Load Points:', e);
            resolve([]);
          });
        }).catch((error) => {
          log.error(error);
        });
      } else {
        reject([]);
      }
    });
  };


  const setSchedules = (prop = []) => {
    let schedules = prop === null ? [] : prop.map((schedule) => new Schedule(schedule));
    setUserSession((prevState) => { prevState.schedules = schedules; return prevState; });
    return schedules;
  };

  const getSchedules = () => {
    return userInfo.schedules;
  };


  const setScheduleDates = (prop = []) => {
    let scheduleDates = prop === null ? [] : prop.map((scheduleDates) => new Schedule(scheduleDates));
    setUserSession((prevState) => { prevState.scheduleDates = scheduleDates; return prevState; });
    return scheduleDates;
  };

  const getScheduleDates = async (day) => {
    return userInfo.scheduleDates;
  };


  const setScheduleTimes = (prop = []) => {
    let scheduleTimes = prop === null ? [] : prop.map((schedulTimee) => new Schedule(schedulTimee));
    setUserSession((prevState) => { prevState.scheduleTimes = scheduleTimes; return prevState; });
    return scheduleTimes;
  };

  const getScheduleTimes = async (day) => {
    return userInfo.scheduleTimes;
  };


  const setPoints = (prop = []) => {
    let points = prop === null ? [] : prop.map((point) => new Point(point))
    setUserSession((prevState) => { prevState.points = points; return prevState; });
    return points;
  };

  const getPoints = () => {
    return userInfo.points;
  };


  const setPlaces = (prop = []) => {
    let places = prop === null ? [] : prop.map((place) => new Place(place))
    setUserSession((prevState) => { prevState.places = places; return prevState; });
    return places;
  };


  const getMyOrders = async (params) => {
    return repository.report(API.cashier.ordersReport, new ReportParams(params), Order).then((report) => {
      return report;
    });
  };

  const getMyUncollectedOrders = async (params) => {
    return repository.report(API.cashier.ordersUncollectedReport, new ReportParams(params), Order).then((report) => {
        return report;
    });
  };

  const getMyCollections = async (params) => {
    return repository.report(API.cashier.collectionsReport, new ReportParams(params), Collection).then((report) => {
      return report;
    });
  };

  const autocompleteOrderClients = async (query) => {
    return repository.list(API.order.autocomplete, { query }, ClientDetails).then((clients) => {
      return clients || [];
    });
  };

  const signIn = (phone, password, again = true) => {
    return new Promise((resolve, reject) => {
      repository.action(API.auth.signIn, { phone, password }).then((response) => {
        if (response) {
          if (response.error) {
            reject({ value: phone, error: t(`common:error.${response.error.toLowerCase()}`) });
          } else {
            setLoggedIn(true);

            getUser().then((user) => {
              if (user && user.isValid()) {
                resolve({ value: phone, error: false });
              } else {
                setLoggedIn(false);
                reject({ value: phone, error: t('common:error.user_not_found')});
              }
            }).catch((e) => {
              setLoggedIn(false);
              reject({ value: phone, error: t('common:error.user_not_found')});
            });
          }
        } else {
          reject({ value: phone, error: t('common:error.unknown_response')});
        }
      }).catch((e) => {
        if (e.error === 11000) {
          removeCookie('session_id');
          removeCookie('x-service');
          signIn(phone, password, false);
          reject({ value: phone, error: t(`common:error.usersessions`) });
        } else {
          reject({ value: phone, error: t(`common:error.${e.message.toLowerCase()}`) });
        }
      });
    });
  };


  const signOut = () => {
    setLoggedIn(false);
    setStorageValue(STORAGE.SESSION, new UserSession());
    _setState('session', null);
    removeCookie('session_id');
    removeCookie('x-service');
    setUser(null);
    setAccount(null);
    return repository.action(API.auth.signOut);
  };

  useEffect(() => {
    if (login) {
      loadUser();
    }
  }, [login]);

  useEffect(() => {
    const session = getStorageValue(STORAGE.SESSION, UserSession);

    _setSession('account', session.account);
    _setSession('role', session.role);
    _setSession('subdivision', session.subdivision);
    _setSession('point', session.point);
    _setSession('logged_in', session.logged_in);

    if (props.logged_in !== undefined) {
      setLoggedIn(props.logged_in);
    } else {
      setLogin(session.logged_in);
    }
  }, []);


  return (
    <UserContext.Provider
      value={{
        getUserInfo: () => userSession,
        reset,

        useSession: _useSession,
        useState: _useState,

        setLoggedIn,
        getSession,
        isLoggedIn,

        getUser,
        setSchedules,
        getSchedules,
        setScheduleDates,
        getScheduleDates,
        setScheduleTimes,
        getScheduleTimes,
        setPoints,
        getPoints,
        getMyOrders,
        getMyUncollectedOrders,
        getMyCollections,
        autocompleteOrderClients,

        loadUser,

        signIn,
        signOut,
        fetch
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};
